/**
 * @file WeeklyChallengePage — hosts the Unity build at both:
 *   /scr-weekly            → the current week   (manifest = current.json)
 *   /scr-weekly/play/:id   → an archived week   (manifest = archive/{id}.json)
 *
 * ONE pipeline. Current and archived render the exact same <ChallengePlayer/>;
 * the ONLY thing that differs is the manifest URL handed to it. That URL is
 * both (a) fetched by the web layer to fill the info cards, and (b) the URL the
 * Unity build's manifest request is redirected to (see unityManifestRouter), so
 * Unity downloads that manifest's skybox / music / track / loading image.
 *
 * There is NO hardcoded competition data here — everything comes from manifests.
 */

import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Hero } from '../components/Hero';
import { UnityPlayer } from '../components/UnityPlayer';
import { ChallengeActions } from '../components/ChallengeActions';
import { CompetitionInfo } from '../components/CompetitionInfo';
import { ArchiveNotice } from '../components/ArchiveNotice';
import { LoadingState, ErrorState } from '../components/StateViews';
import { useArchivedById, useManifestByUrl } from '../hooks/data';
import { CURRENT_MANIFEST_URL } from '../config/scrWeeklyConfig';
import {
  installRaceResultBridge,
  setUnityInstance,
  setArchivedMode,
  setArchivedNoticeHandler,
} from '../services/raceResultBridge';
import {
  installManifestInterceptor,
  uninstallManifestInterceptor,
  setActiveManifestUrl,
} from '../services/unityManifestRouter';
import { logLaunch, logCompetition } from '../services/diagnostics';
import { formatDate, seasonWeekLabel } from '../utils/format';
import '../styles/weeklyChallenge.scss';

/**
 * The one game stage used by both current and archived play.
 * @param {{ manifestUrl: string, archived: boolean }} props
 */
function ChallengePlayer({ manifestUrl, archived }) {
  /** @type {React.MutableRefObject<import('../components/UnityPlayer').UnityPlayerHandle|null>} */
  const playerRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [showArchiveNotice, setShowArchiveNotice] = useState(false);

  // Fetch THIS manifest (same one Unity will load) for the info cards.
  const { data: competition } = useManifestByUrl(manifestUrl);

  // Install the bridge + interceptor for the lifetime of this view.
  useEffect(() => {
    installRaceResultBridge();
    installManifestInterceptor();
    return () => {
      uninstallManifestInterceptor();
    };
  }, []);

  // Point Unity's manifest request at THIS manifest — but ONLY for archived
  // weeks. For the current week Unity's OWN baked current.json URL is already
  // correct, so we must NOT redirect it (redirecting to the web-config URL would
  // send Unity to the wrong/sample manifest). Runs on mount, before Play.
  useEffect(() => {
    setActiveManifestUrl(archived ? manifestUrl : null);
    setArchivedMode(archived);
  }, [manifestUrl, archived]);

  // Archived: reveal the notice when a race finishes (submission is skipped).
  useEffect(() => {
    if (!archived) return undefined;
    setArchivedNoticeHandler(() => setShowArchiveNotice(true));
    return () => setArchivedNoticeHandler(null);
  }, [archived]);

  // Diagnostics: confirm which competition was actually fetched + parsed.
  useEffect(() => {
    if (competition) logCompetition(competition);
  }, [competition]);

  const handleStart = () => {
    logLaunch(manifestUrl, { archived });
    setStarted(true);
  };

  const handlePlayFullscreen = () => {
    const player = playerRef.current;
    if (!player) return;
    if (started) player.enterFullscreen();
    else player.start();
  };

  const hero = archived
    ? {
        title: competition ? competition.title : 'Archive Challenge',
        subtitle: competition
          ? `Archive Challenge · ${seasonWeekLabel(competition.season, competition.week)}`
          : 'Archive Challenge',
        description: competition
          ? competition.description || competition.theme
          : 'Replaying a previous weekly challenge.',
      }
    : { title: 'Sky City Rush', subtitle: 'Weekly Challenge' };

  const info = competition
    ? {
        theme: competition.theme,
        title: competition.title,
        description: competition.description || '—',
        generatedAt: formatDate(competition.generatedAt),
      }
    : { theme: '—', title: '—', description: '—', generatedAt: '—' };

  return (
    <div className="scrw-challenge">
      <Hero {...hero} />

      <section className="scrw-challenge__stage" aria-label="Game">
        <div className="scrw-unity-wrap">
          <UnityPlayer
            ref={playerRef}
            aspectRatio="16 / 10"
            onStart={handleStart}
            onReady={setUnityInstance}
          />
          {archived && showArchiveNotice ? (
            <ArchiveNotice onClose={() => setShowArchiveNotice(false)} />
          ) : null}
        </div>
        <ChallengeActions
          started={started}
          archived={archived}
          onPlayFullscreen={handlePlayFullscreen}
        />
      </section>

      <CompetitionInfo
        theme={info.theme}
        title={info.title}
        description={info.description}
        generatedAt={info.generatedAt}
      />
    </div>
  );
}

/** Current week → the current manifest. */
function CurrentChallengeView() {
  return <ChallengePlayer manifestUrl={CURRENT_MANIFEST_URL} archived={false} />;
}

/** Archived week → resolve id to its manifest URL, then play it. */
function ArchivedChallengeView({ id }) {
  const { data, loading, error } = useArchivedById(id);

  if (loading) {
    return (
      <div className="scrw-challenge">
        <LoadingState label="Loading archived challenge…" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="scrw-challenge">
        <div className="scrw-page-head">
          <h1 className="scrw-page-head__title">Archive Challenge</h1>
        </div>
        <ErrorState label="This challenge is no longer available." />
      </div>
    );
  }

  return <ChallengePlayer manifestUrl={data.manifestUrl} archived />;
}

export function WeeklyChallengePage() {
  const { id } = useParams();
  return id ? <ArchivedChallengeView id={id} /> : <CurrentChallengeView />;
}
